import { qfRestGet } from './qfClient';
import type { TickerStats } from './optionContractsService';

/**
 * `GET /v1/symbol-summary` — the whole Ticker Explorer Overview card in one
 * request: header, Range, Volume and Options Flow.
 *
 * QuantForge widened this endpoint rather than adding a per-panel family, so
 * one symbol is one call and every figure on the card refreshes together.
 * Company name and sector are deliberately NOT here — they sit on
 * `/v1/company-profile` (`max-age=3600`), because polling a static string
 * every fifteen seconds is waste.
 *
 * ### Ratios are derived here, not served
 *
 * QF returns components and no ratios: relative volume, % of average, % of
 * range and put/call are all computed client-side. That is deliberate on their
 * side and worth preserving on ours — both operands arrive in the same
 * response from the same query group, so same-row provenance is structural
 * rather than a convention someone has to remember. A ratio assembled from a
 * separately-fetched baseline can drift windows between numerator and
 * denominator and still render a plausible number.
 */

/** ⚠️ The wire shape. `snake_case`, and percentages are FRACTIONS. */
export interface SymbolSummary {
  symbol: string;

  /* ─ header ─ */
  last: number | null;
  change: number | null;
  /**
   * PERCENT — −0.12 is −0.12%.
   *
   * ⚠️ **Not the same unit as `change_5d_pct` below**, which really is a
   * fraction. Different services on QuantForge's side produce them. This was
   * documented here as a fraction until 2026-08-06 and the day change rendered
   * 100x too large as a result.
   */
  change_pct: number | null;
  /** FRACTION — 0.0343 is +3.43%. Genuinely differs from `change_pct`. */
  change_5d_pct: number | null;
  market_cap: number | null;
  next_earnings: string | null;
  /** `weekend | holiday | premarket | open | postmarket` */
  session_phase: string | null;

  /* ─ range ─ */
  prev_close: number | null;
  week52_high: number | null;
  week52_low: number | null;

  /* ─ volatility ─ */
  iv30: number | null;
  iv60: number | null;
  iv90: number | null;
  hv10: number | null;
  hv20: number | null;
  hv30: number | null;
  hv60: number | null;

  /* ─ share volume ─ */
  /** ⚠️ Null for index symbols by design — see `toTickerStats`. */
  share_volume: number | null;
  /** 30 CALENDAR days (~22 sessions), UW's window. */
  avg30_share_volume: number | null;

  /* ─ options ─ */
  call_volume: number | null;
  put_volume: number | null;
  total_options_volume: number | null;
  /** 30 calendar days, UW's window. */
  call_volume_30d: number | null;
  /** 30 calendar days, UW's window. */
  put_volume_30d: number | null;
  net_call_premium: number | null;
  net_put_premium: number | null;
  /**
   * QuantForge's canonical net premium, despite the name.
   *
   * Computed server-side as `net_call_premium − net_put_premium`, where both
   * operands are ALREADY signed ask-minus-bid per side. That expands to
   * `(call_ask + put_bid) − (call_bid + put_ask)` — the canonical directional
   * formula exactly, with puts sold counting bullish.
   *
   * ⚠️ It is NOT the aggression figure `(call_ask + put_ask) − (call_bid +
   * put_bid)`, which scores bearish put buying positive. The two are easy to
   * confuse because this one reads like "calls minus puts" at a glance; the
   * difference is that the operands here are signed, not gross, so the sign
   * flip on `put_bid` is applied twice and cancels.
   *
   * Safe to label "Net Premium" in the UI.
   */
  pressure: number | null;
}

/**
 * A fraction as a percentage, preserving null.
 *
 * ⚠️ **Only for fields that really are fractions.** `change_pct` and
 * `change_5d_pct` read like a matched pair and are not — see `toSymbolHeader`.
 */
function pct(n: number | null | undefined): number | null {
  return n == null ? null : n * 100;
}

/** `a / b`, null unless both are known and the denominator is usable. */
function ratio(a: number | null | undefined, b: number | null | undefined): number | null {
  if (a == null || b == null || b <= 0) return null;
  return a / b;
}

/** `a + b`, null unless BOTH are known — a half-known sum is not a total. */
function sum(a: number | null | undefined, b: number | null | undefined): number | null {
  if (a == null || b == null) return null;
  return a + b;
}

/**
 * Wire shape → the `TickerStats` the Volume and Options Flow columns consume.
 *
 * ⚠️ **`share_volume` is null for index symbols and that is not an error.**
 * SPX/NDX/RUT/VIX have no share volume structurally, so QF nulls it rather
 * than returning a confident zero. Null must render as a dash, never as "no
 * volume traded" — which is why nothing here coerces a missing figure to 0.
 *
 * ⚠️ **The 30-day averages are 30 CALENDAR days (~22 sessions)**, from UW's
 * dimension feed. That is intended: the labels say "30d" and mean it. Do not
 * "correct" these to 30 trailing sessions — the ratios below would then be
 * measured against a window a third wider than the one the label names.
 */
export function toTickerStats(s: SymbolSummary): TickerStats {
  const avgOptionsVolume = sum(s.call_volume_30d, s.put_volume_30d);

  return {
    date: null,

    shareVolume: s.share_volume,
    avgShareVolume: s.avg30_share_volume,
    relativeVolume: ratio(s.share_volume, s.avg30_share_volume),

    callVolume: s.call_volume,
    putVolume: s.put_volume,
    totalOptionsVolume: s.total_options_volume,
    avgOptionsVolume,
    netCallPremium: s.net_call_premium,
    netPutPremium: s.net_put_premium,
    netPremium: s.pressure,

    // ⚠️ Not served by this endpoint. Nothing on the Overview card reads them
    // today; they stay null rather than being faked from another source.
    callOpenInterest: null,
    putOpenInterest: null,

    putCall: ratio(s.put_volume, s.call_volume),
  };
}

/** Where the header's 52-week band and 5-day badge come from. */
export interface SymbolHeader {
  last: number | null;
  change: number | null;
  /** PERCENT. Arrives as a percent and is passed through — see `toSymbolHeader`. */
  changePct: number | null;
  /** PERCENT. Arrives as a FRACTION and is scaled — see `toSymbolHeader`. */
  change5dPct: number | null;
  marketCap: number | null;
  nextEarnings: string | null;
  sessionPhase: string | null;
  prevClose: number | null;
  week52High: number | null;
  week52Low: number | null;
}

/**
 * Wire shape → the header figures, in percentages.
 *
 * ⚠️ **`change_pct` and `change_5d_pct` do NOT share a unit**, despite the
 * matching names. They are produced by different services on QuantForge's side:
 *
 *   `change_pct`      already a PERCENT   `(last - prev) / prev * 100`
 *   `change_5d_pct`   a FRACTION          `closes[-1] / closes[-6] - 1.0`
 *
 * So exactly one of them is scaled here. Scaling both — which this function
 * did until 2026-08-06 — renders the day change 100x too large: NVDA showed
 * −12.00% for a −0.27 move on 218.95, which is −0.12%. It looks like a
 * catastrophic session rather than a quiet one, and nothing errors.
 *
 * ⚠️ QuantForge warns about this collision in `rth_quote.py`: their
 * ClickHouse-sourced paths emit percent, their FMP/report-builder branch emits
 * a fraction, **under the same field name**. Do not assume a `_pct` suffix
 * implies either unit — check which service produced the field.
 *
 * The cheap check is arithmetic on one response: `change / last * 100` must
 * land on `changePct`. It is the same session, same instant, so any
 * disagreement is a unit error rather than staleness.
 */
export function toSymbolHeader(s: SymbolSummary): SymbolHeader {
  return {
    last: s.last,
    change: s.change,
    // Already a percent on the wire — passed through, NOT scaled.
    changePct: s.change_pct,
    change5dPct: pct(s.change_5d_pct),
    marketCap: s.market_cap,
    nextEarnings: s.next_earnings,
    sessionPhase: s.session_phase,
    prevClose: s.prev_close,
    week52High: s.week52_high,
    week52Low: s.week52_low,
  };
}

/** Where a price sits in the 52-week band. Percentages, 0–100 for `pct`. */
export interface HeaderRange {
  low: number;
  high: number;
  /** Position across the band, 0–100. */
  pct: number;
  /** Percent below the high. Positive on a new-high day — see below. */
  fromHighPct: number;
}

/**
 * The 52-week band and where `last` sits in it.
 *
 * ⚠️ **The band is refreshed on change, roughly daily; `last` is live.** On a
 * day a name prints a NEW 52-week high, the live price is above a band edge
 * that has not been restated yet. That is not a feed bug — it is a live number
 * against a stale one.
 *
 * Both edges are widened to admit the live price rather than clamping `pct`
 * alone. Clamping only the meter would pin the dot at 100% while "From High"
 * showed a positive percentage, which reads as a rendering fault; widening
 * keeps the meter and the figure telling the same story. `fromHighPct` is
 * deliberately left unclamped and so goes positive on a new high, which is the
 * honest display.
 */
export function headerRange(h: SymbolHeader): HeaderRange | null {
  const { week52Low: lo, week52High: hi, last } = h;
  if (lo == null || hi == null || last == null) return null;

  const low = Math.min(lo, last);
  const high = Math.max(hi, last);
  const span = high - low;

  return {
    low,
    high,
    // A flat year would divide by zero; treat it as sitting at the top.
    pct: span > 0 ? ((last - low) / span) * 100 : 100,
    // Measured against the UNWIDENED high, so a new high reads positive
    // rather than being silently flattened to zero.
    fromHighPct: hi > 0 ? (last / hi - 1) * 100 : 0,
  };
}

/**
 * True when the session phase means the tape is not running.
 *
 * Prefers the server's phase over a client clock — the browser's timezone,
 * and its idea of which days are holidays, are both things we would otherwise
 * be guessing at. Null phase falls back to the caller's own judgement.
 */
export function isClosedPhase(phase: string | null | undefined): boolean | null {
  if (phase == null) return null;
  return phase === 'weekend' || phase === 'holiday';
}

/**
 * One symbol's whole Overview card.
 *
 * ⚠️ **A symbol with no data is a 200 with null fields, not a 404.** An
 * unlisted or newly-listed name legitimately has no stats and the card shows
 * dashes. The endpoint also degrades per group — a datastore problem nulls one
 * panel's fields rather than failing the response — so a partly-dashed card is
 * expected behaviour under degradation and not something to retry harder.
 */
export async function fetchSymbolSummary(symbol: string): Promise<SymbolSummary> {
  return qfRestGet<SymbolSummary>('/v1/symbol-summary', {
    symbol: symbol.trim().toUpperCase(),
  });
}
